/**
 * installmentStatus — състоянието на една вноска СЕГА.
 *
 * 🔴 Записаното `installment.status` е моментна снимка: пише се само когато таксата на
 * семейството се преизчисли (плащане, „Преизчисли всички такси“). Минава ли падежът
 * без преизчисляване, полето остава „upcoming“. Измерено на staging 07.10.2026: три
 * семейства, неприпипвани от 07.09, пишеха „upcoming“ за вноска с падеж 30.09 — и
 * „Просрочени“ на таблото ги пропускаше.
 *
 * Затова четецът не чете полето, а пита тук: състоянието се извежда от падежа, платеното
 * и БАЛАНСА на семейството. Писачите (преизчисляването) също минават оттук, тоест записаното
 * е същото число, само по-старо. Гардът е `installmentStatusRatchet.test.ts`.
 *
 * Дните са КАЛЕНДАРНИ, по Europe/Vienna (TASK_54 В5, owner 07.10):
 * · „просрочена“ — от деня СЛЕД падежа (падеж 30.09 → от 01.10 00:00 по Виена);
 * · „наближава“ — от 10-ия календарен ден преди падежа до края на падежния ден включително
 *   (11 календарни дни; досега — 10 × 24 ч до момента на падежа);
 * · иначе „предстои“.
 * Падежите се пазят като UTC полунощ (`new Date('YYYY-MM-DD')`); виенският ден на този момент е
 * СЪЩАТА дата. Сравнението по дни не зависи от лятното/зимното време.
 *
 * 🔴 Баланс ≤ 0 → всяка вноска е „платена“ (TASK_54 В7): семейство, което не дължи нищо, не
 * показва неплатена вноска, каквото и да пише в записа — старо деление (288 + 288 при 575),
 * ръчно сменено дължимо, запис от клиента. Аргументът е ЗАДЪЛЖИТЕЛЕН: четец без баланс не се
 * компилира.
 */
import type { InstallmentStatus } from '../types/fees';
import { toMillisOrNull } from './millis';

const DAY_MS = 24 * 60 * 60 * 1000;
/** От колко календарни дни преди падежа вноската е „due“ (наближава). */
export const INSTALLMENT_DUE_SOON_DAYS = 10;
/** Часовата зона на календарния ден — училището е във Виена. */
export const INSTALLMENT_TIME_ZONE = 'Europe/Vienna';

const viennaDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: INSTALLMENT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Поредният номер на календарния ден (по Виена), в който попада моментът `ms`. */
export function viennaDayNumber(ms: number): number {
  const parts = viennaDate.formatToParts(new Date(ms));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(part('year'), part('month') - 1, part('day')) / DAY_MS;
}

export function installmentStatus(
  installment: { dueDate: unknown; amount: number; paidAmount: number; status?: InstallmentStatus },
  familyBalance: number,
  nowMs: number = Date.now(),
): InstallmentStatus {
  if (familyBalance <= 0) return 'paid';
  if (installment.paidAmount >= installment.amount) return 'paid';

  const dueMs = toMillisOrNull(installment.dueDate);
  // Без четим падеж няма от какво да се изведе — остава записаното.
  if (dueMs === null) return installment.status ?? 'upcoming';

  const today = viennaDayNumber(nowMs);
  const dueDay = viennaDayNumber(dueMs);
  if (today > dueDay) return 'overdue';
  if (today >= dueDay - INSTALLMENT_DUE_SOON_DAYS) return 'due';
  return 'upcoming';
}
